/*
 * <product-spin> — interactive 360° turntable viewer built from an image sequence.
 *
 * Frames are provided as a JSON array of asset URLs in a
 * <script type="application/json" data-spin-frames> child. The element preloads
 * them progressively and lets the user drag / swipe / arrow-key through the
 * rotation. It slowly auto-spins until the first interaction (unless the visitor
 * prefers reduced motion). Follows the theme's Web Component + connectedCallback
 * convention — no globals, cleans up in disconnectedCallback.
 */
if (!customElements.get("product-spin")) {
  customElements.define(
    "product-spin",
    class ProductSpin extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.image = this.querySelector("[data-spin-image]");
        this.progress = this.querySelector("[data-spin-progress]");
        this.hint = this.querySelector("[data-spin-hint]");

        // Frames are pre-cropped to the subject, so their aspect ratio varies
        // per product. Reserve the box from the first frame's own dimensions so
        // there's no layout jump when it paints.
        if (this.image) {
          const applyAspect = () => {
            if (this.image.naturalWidth && this.image.naturalHeight) {
              this.style.setProperty(
                "--spin-aspect",
                this.image.naturalWidth + " / " + this.image.naturalHeight,
              );
            }
          };
          if (this.image.complete && this.image.naturalWidth) applyAspect();
          else this.image.addEventListener("load", applyAspect, { once: true });
        }

        const framesEl = this.querySelector("[data-spin-frames]");
        try {
          this.frames = JSON.parse(framesEl.textContent);
        } catch (e) {
          this.frames = [];
        }
        this.count = this.frames.length;
        if (this.count === 0) return;

        // Higher sensitivity = more rotation for the same drag distance.
        this.sensitivity = parseFloat(this.dataset.sensitivity) || 1.5;
        this.reverse = this.dataset.reverse === "true";
        this.autospin = this.dataset.autospin !== "false";
        this.reducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        this.index = 0;
        this.frame = 0; // fractional position used by auto-spin / inertia
        this.loaded = new Array(this.count).fill(false);
        this.loadedCount = 0;
        this.dragging = false;
        this.interacted = false;
        this.pointerId = null;
        this.rafId = null;
        this.velocity = 0; // frames per ms, for inertia
        this.lastMoveTime = 0;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.tick = this.tick.bind(this);

        this.addEventListener("pointerdown", this.onPointerDown);
        this.addEventListener("keydown", this.onKeyDown);

        this.preload();
        this.setupVisibility();
      }

      disconnectedCallback() {
        this.stopRaf();
        this.removeEventListener("pointerdown", this.onPointerDown);
        this.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
        window.removeEventListener("pointercancel", this.onPointerUp);
        if (this.io) this.io.disconnect();
      }

      preload() {
        // Load in a spread-out order so a partial rotation looks smooth early on.
        const order = [];
        const stride = Math.max(1, Math.round(this.count / 12));
        for (let offset = 0; offset < stride; offset += 1) {
          for (let i = offset; i < this.count; i += stride) order.push(i);
        }

        order.forEach((i) => {
          const img = new Image();
          img.onload = img.onerror = () => {
            this.loaded[i] = true;
            this.loadedCount += 1;
            this.updateProgress();
          };
          img.src = this.frames[i];
        });
      }

      updateProgress() {
        const pct = Math.round((this.loadedCount / this.count) * 100);
        if (this.progress)
          this.progress.style.setProperty("--spin-progress", `${pct}%`);
        if (this.loadedCount >= this.count) {
          this.classList.add("product-spin--ready");
          if (this.autospin && !this.reducedMotion && !this.interacted)
            this.startAutospin();
        }
      }

      setupVisibility() {
        if (!("IntersectionObserver" in window)) return;
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          if (!this.visible) {
            this.stopRaf();
          } else if (this.autospinning && !this.dragging) {
            this.startAutospin();
          }
        });
        this.io.observe(this);
      }

      startAutospin() {
        if (this.reducedMotion || this.interacted) return;
        this.autospinning = true;
        this.mode = "autospin";
        this.lastFrameTime = null;
        this.startRaf();
      }

      startRaf() {
        this.stopRaf();
        this.rafId = requestAnimationFrame(this.tick);
      }

      stopRaf() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      tick(now) {
        if (this.lastFrameTime == null) this.lastFrameTime = now;
        const dt = now - this.lastFrameTime;
        this.lastFrameTime = now;

        if (this.mode === "autospin") {
          // ~one full rotation every 3.6s
          const framesPerMs = this.count / 3600;
          this.frame += framesPerMs * dt * (this.reverse ? -1 : 1);
          this.setFrame(this.frame);
          this.rafId = requestAnimationFrame(this.tick);
          return;
        }

        if (this.mode === "inertia") {
          this.frame += this.velocity * dt;
          this.velocity *= Math.pow(0.9, dt / 16); // frame-rate independent decay
          this.setFrame(this.frame);
          if (Math.abs(this.velocity) < 0.014) {
            this.stopRaf();
            return;
          }
          this.rafId = requestAnimationFrame(this.tick);
        }
      }

      onPointerDown(event) {
        if (event.button != null && event.button !== 0) return;
        this.markInteracted();
        this.stopRaf();
        this.dragging = true;
        this.pointerId = event.pointerId;
        this.startX = event.clientX;
        this.startFrame = this.frame;
        this.velocity = 0;
        this.lastMoveTime = event.timeStamp;
        this.lastMoveFrame = this.frame;
        this.classList.add("product-spin--dragging");
        // One full drag across the element ≈ `sensitivity` rotations.
        this.pxPerFrame = this.clientWidth / (this.count * this.sensitivity);
        if (this.pxPerFrame <= 0) this.pxPerFrame = 5;
        try {
          this.setPointerCapture(event.pointerId);
        } catch (e) {
          /* not all pointer types support capture */
        }
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
        window.addEventListener("pointercancel", this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.dragging || event.pointerId !== this.pointerId) return;
        event.preventDefault();
        const dx = event.clientX - this.startX;
        // Drag right → object rotates as if pushed to the right.
        const dir = this.reverse ? 1 : -1;
        const newFrame = this.startFrame + (dx / this.pxPerFrame) * dir;

        const dtMove = event.timeStamp - this.lastMoveTime;
        if (dtMove > 0)
          this.velocity = (newFrame - this.lastMoveFrame) / dtMove;
        this.lastMoveTime = event.timeStamp;
        this.lastMoveFrame = newFrame;

        this.frame = newFrame;
        this.setFrame(this.frame);
      }

      onPointerUp(event) {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove("product-spin--dragging");
        window.removeEventListener("pointermove", this.onPointerMove);
        window.removeEventListener("pointerup", this.onPointerUp);
        window.removeEventListener("pointercancel", this.onPointerUp);

        // Fling → inertia; otherwise settle on the nearest frame.
        if (Math.abs(this.velocity) > 0.003) {
          this.mode = "inertia";
          this.lastFrameTime = null;
          this.startRaf();
        } else {
          this.frame = Math.round(this.frame);
          this.setFrame(this.frame);
        }
      }

      onKeyDown(event) {
        let step = 0;
        if (event.key === "ArrowRight") step = this.reverse ? -1 : 1;
        else if (event.key === "ArrowLeft") step = this.reverse ? 1 : -1;
        else return;
        event.preventDefault();
        this.markInteracted();
        this.stopRaf();
        this.frame = Math.round(this.frame) + step;
        this.setFrame(this.frame);
      }

      markInteracted() {
        if (this.interacted) return;
        this.interacted = true;
        this.autospinning = false;
        this.classList.add("product-spin--interacted");
      }

      // Wrap to a valid frame index and show it. Falls back to the nearest
      // already-loaded frame so scrubbing never shows a blank image.
      setFrame(value) {
        let idx = Math.round(value) % this.count;
        if (idx < 0) idx += this.count;

        if (!this.loaded[idx]) {
          let nearest = idx;
          for (let d = 1; d < this.count; d += 1) {
            const up = (idx + d) % this.count;
            const down = (idx - d + this.count) % this.count;
            if (this.loaded[up]) {
              nearest = up;
              break;
            }
            if (this.loaded[down]) {
              nearest = down;
              break;
            }
          }
          if (!this.loaded[nearest]) return;
          idx = nearest;
        }

        if (idx === this.index) return;
        this.index = idx;
        this.image.src = this.frames[idx];
      }
    },
  );
}
